// src/components/Layout/Navigation.jsx - UPDATED WITH Z-INDEX FIX
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import gsap from 'gsap';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const overlayRef = useRef(null);

  const navItems = [
    { name: 'Home', path: '/' },
    { 
      name: 'About', 
      path: '/about',
      // subItems: [
      //   { name: 'Our Story', path: '/about#story' },
      //   { name: 'Leadership', path: '/about#leadership' },
      //   { name: 'Values', path: '/about#values' },
      // ]
    },
    { name: 'Our Process', path: '/process' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Certifications', path: '/certifications' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setIsOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
    };
  }, [isOpen]);

  // Close menu on ESC key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Prevent scrolling on overlay
  const handleOverlayTouch = (e) => {
    e.preventDefault();
    setIsOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden lg:flex items-center space-x-1">
        {navItems.map((item) => (
          <div key={item.name} className="relative group">
            <Link
              to={item.path}
              className={`px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors duration-300 ${
                location.pathname === item.path
                  ? 'text-gold-400'
                  : 'text-gray-300 hover:text-gold-300'
              }`}
            >
              {item.name}
              {item.subItems && <ChevronDown className="inline w-4 h-4 ml-1" />}
            </Link>
            
            {/* Hover underline */}
            <div
              className={`absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-gold-400 to-gold-600 transition-all duration-300 ${
                location.pathname === item.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}
            />
            
            {/* Dropdown for sub-items */}
            {item.subItems && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-dark-900/95 backdrop-blur-md border border-gold-500/20 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[100]">
                {item.subItems.map((subItem) => (
                  <Link
                    key={subItem.name}
                    to={subItem.path}
                    className="block px-4 py-3 text-sm text-gray-300 hover:text-gold-400 hover:bg-gold-500/10 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {subItem.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
        
        {/* CTA Button */}
        <div className="ml-4">
          <Link
            to="/contact"
            className="px-6 py-2.5 bg-gradient-to-r from-gold-600 to-gold-700 text-dark-900 font-semibold rounded-full hover:from-gold-500 hover:to-gold-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Partner With Us
          </Link>
        </div>
      </nav>

      {/* Mobile Menu Button */}
      <button
        className="lg:hidden p-2 text-gold-400 z-[9999] relative"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        {isOpen ? <X size={28} /> : <Menu size={28} />}
      </button>

      {/* Mobile Navigation Overlay - HIGHER Z-INDEX */}
      <div
        ref={overlayRef}
        className={`fixed inset-0 z-[9990] bg-black/90 backdrop-blur-lg transition-all duration-300 lg:hidden ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsOpen(false)}
        onTouchMove={handleOverlayTouch}
        aria-hidden={!isOpen}
        style={{
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      />

      {/* Mobile Navigation Drawer - EVEN HIGHER Z-INDEX */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 bottom-0 z-[9995] w-80 max-w-full bg-dark-900/98 backdrop-blur-xl border-l border-gold-500/30 transform transition-all duration-300 ease-out lg:hidden ${
          isOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full'
        }`}
        style={{
          boxShadow: isOpen ? '0 0 50px rgba(212, 175, 55, 0.2)' : 'none',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header - Fixed position */}
          <div className="p-6 border-b border-gold-500/30 bg-dark-900/80 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <Link 
                to="/" 
                className="text-2xl font-bold gold-gradient"
                onClick={() => setIsOpen(false)}
              >
                ProFit Mix
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="p-3 rounded-full bg-gold-500/10 hover:bg-gold-500/20 transition-colors"
                aria-label="Close menu"
              >
                <X size={24} className="text-gold-400" />
              </button>
            </div>
            <p className="text-xs text-gold-300/70 tracking-widest uppercase mt-2">
              Next Generation Protein
            </p>
          </div>

          {/* Navigation Items - Scrollable area */}
          <div className="flex-1 overflow-y-auto py-4 px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-1 px-4">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    className={`block px-4 py-4 rounded-xl transition-all duration-200 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-gold-600/20 to-gold-800/10 text-gold-400 border-l-4 border-gold-500'
                        : 'text-gray-300 hover:bg-gold-500/10 hover:text-gold-300 hover:border-l-4 hover:border-gold-500/50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-lg">{item.name}</span>
                      {item.subItems && <ChevronDown className="w-5 h-5 text-gold-400/50" />}
                    </div>
                  </Link>
                  
                  {/* Mobile Sub-items */}
                  {item.subItems && (
                    <div className="ml-8 mt-1 mb-3 space-y-1">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block px-4 py-3 text-gray-400 hover:text-gold-300 rounded-lg hover:bg-gold-500/5 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="mt-8 pt-8 border-t border-gold-500/20 px-6">
              <Link
                to="/contact"
                className="block w-full px-6 py-4 bg-gradient-to-r from-gold-600 to-gold-700 text-dark-900 font-bold rounded-xl text-center hover:from-gold-500 hover:to-gold-600 transition-all duration-300 transform hover:scale-[1.02] shadow-lg active:scale-95"
                onClick={() => setIsOpen(false)}
              >
                Partner With Us
              </Link>
              <p className="text-xs text-gray-400 text-center mt-4">
                Building the future of sustainable protein
              </p>
            </div>
          </div>


        </div>
      </div>
    </>
  );
};

export default Navigation;